// src/components/Purchase/SearchBar/index.tsx

import React, { KeyboardEvent, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, Icon, SearchField } from '@gerdau/hefesto';
import { useDispatch, useSelector } from 'react-redux';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import {
  CartItemPayload,
  ProductDescription,
  SearchOptionsProps
} from '../../../services/MVP/models';
import useDebounce from '../../../hooks/useDebounce';
import { ISearchResult } from '@gerdau/hefesto/SearchField/SearchField.types';
import useCatalogService from '../../../services/MVP/useCatalogService';
import { ApplicationState } from '../../../store';
import { useProductForCustomer } from '../../../services/MVP/useProductForCustomer';
import { useCart } from '../../../pages/Purchase/LongSteel/ShoppingCartMVP/useCart';
import { useCartService } from '../../../services/MVP/useCartService';
import { addToast } from '../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../store/ducks/toast/types';
import { useLocation } from 'react-router-dom';
import { getTaggingInformationScreen } from '../../../utils/Tagging';
import { updateCartItemsQuantity } from '../../../store/ducks/cart-items-mvp/actions';
import { GetFlag } from '../../../utils/featureFlag';
import {
  setTipoCompraForSku,
  setLastTipoCompra
} from '../../../services/MVP/tipoCompraMap';

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  padding: 0.5rem;
  gap: 0.75rem;
  button {
    height: 48px;
  }
`;

interface SearchBarProps {
  handleSearchChange: (
    value: SearchOptionsProps['filters']
  ) => Promise<void | null>;
  searchOptions: SearchOptionsProps;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const SearchBar = ({
  handleSearchChange,
  searchOptions,
  setLoading
}: SearchBarProps) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLInputElement | null>(null);
  const [searchProductsList, setSearchProductsList] = useState<ISearchResult[]>(
    []
  );
  const { searchProducts, sortProducts } = useCatalogService();
  const { fetchProductDetailByCatalog } = useProductForCustomer();
  const { addItemToCart } = useCart();
  const { createNewCart, getCartId } = useCartService();
  const { emitterId, receiverId } = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );
  const { pathname } = useLocation();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);
  const { customers } = useSelector(
    (state: ApplicationState) => state.customers
  );
  const selectedCustomer = customers.find((c) => c.customerId === emitterId);
  const MAX_PAGE_LEN = 120;
  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchByString(query);
    }
    if (e.key === 'Escape' && ref.current) {
      ref.current.blur();
    }
  };

  const searchByString = async (searchQuery: string) => {
    setQuery('');
    await handleSearchChange({
      input: !searchQuery ? query : searchQuery
    });
    dispatch(
      sendTagging({
        event: 'view_search_results',
        screenName: 'vitrine',
        etapa: 'pesquisa_vitrine',
        search_term: query
      })
    );
  };

  const handleOnChange = async (value: string) => {
    const query = value.trim();
    if (query.length > 2 && emitterId && selectedCustomer) {
      const hasProductsTypes =
        searchOptions.filters.productsTypes &&
        searchOptions.filters.productsTypes.filter((f) => f.length > 0);

      let productDescriptionData: {
        products: ProductDescription[];
        total: number;
      } = {
        products: [],
        total: 0
      };

      if (newSearchItemsEndpointFlag) {
        searchOptions.suggest = true;
        productDescriptionData = await sortProducts({
          sortType: searchOptions.filters.ordenation,
          pageSize: MAX_PAGE_LEN,
          ...(['R', 'AC', 'AB'].includes(
            searchOptions.filters.ordenation ?? ''
          ) && {
            currentPage: searchOptions.pagination.currentPage
          }),
          options: {
            ...searchOptions,
            filters: {
              ...searchOptions.filters,
              input: query
            }
          }
        });
      } else {
        if (searchOptions.filters.materialCode === 'gerdauCode') {
          productDescriptionData = await searchProducts({
            ...(hasProductsTypes && {
              sap_descricao_gpm: searchOptions.filters.productsTypes?.filter(
                (f) => f.length > 0
              )
            }),
            ...(searchOptions.filters.ordenation === 'AC' && {
              searchType: 'AC'
            }),
            sku: query,
            sortType: searchOptions.filters.ordenation,
            currentPage: searchOptions.pagination.currentPage,
            pageSize: MAX_PAGE_LEN
          });
        } else {
          productDescriptionData = await searchProducts({
            ...(hasProductsTypes && {
              sap_descricao_gpm: searchOptions.filters.productsTypes?.filter(
                (f) => f.length > 0
              )
            }),
            ...(searchOptions.filters.ordenation === 'AC' && {
              searchType: 'AC'
            }),
            internalCode: query,
            sortType: searchOptions.filters.ordenation,
            currentPage: searchOptions.pagination.currentPage,
            pageSize: MAX_PAGE_LEN
          });
        }
      }
      if (productDescriptionData.products.length) {
        setSearchProductsList(
          productDescriptionData.products.map((product, i) => {
            const internalCodeQuery =
              searchOptions.filters.materialCode === 'internalCode' &&
              product.internalCode;
            const label = `${String(
              internalCodeQuery ? product.internalCode : product.sku
            ).toLocaleUpperCase('pt-BR')} - ${product.name.toLocaleUpperCase(
              'pt-BR'
            )}`;

            return {
              id: `${product.sku}-${i}`,
              label
            } as ISearchResult;
          })
        );
      }
      // Fazendo isso para o dropdown aparecer após response
      if (ref.current) {
        ref.current.blur();
        ref.current.focus();
      }
    }
  };
  const debouncedQuery = useDebounce(handleOnChange, 800);

  const handleSelectedProduct = async (resultOption: ISearchResult) => {
    dispatch(
      sendTagging({
        event: 'view_search_results',
        screenName: 'vitrine',
        etapa: 'pesquisa_vitrine',
        search_term: resultOption.label
      })
    );
    try {
      setLoading(true);

      const sku = resultOption.id.split('-')[0].trim();
      const product = await fetchProductDetailByCatalog(sku);

      if (
        !product ||
        !product.price ||
        Number(product.price.creditPrice) <= 0
      ) {
        return dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: `Produto indisponível. (SKU ${product?.sku ?? sku})`,
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }

      const existingCart = getCartId(emitterId);
      let cart;
      let currentQuantity;

      if (!existingCart || existingCart.length === 0) {
        const newCart = await createNewCart(emitterId, receiverId, true);
        cart = newCart?.cartId;
        currentQuantity = 1;
      } else {
        cart = existingCart;
        currentQuantity = cartQuantity + 1;
      }

      if (!cart || cart.length === 0) {
        throw new Error();
      }

      if (currentQuantity > 30) {
        return dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }

      const formattedProduct = {
        sku: product.sku,
        qty: 1,
        quote_id: cart,
        extension_attributes: {
          measure_unity_gerdau: product.price.um,
          destination_gerdau: product.price.destination,
          payment_type_gerdau: product.price.paymentTerms,
          has_agreed_price_gerdau: product.price.isAgreementPrice.length !== 0,
          ...(product.price.salesAgreementData.expirationDate && {
            price_agreement_date_gerdau:
              product.price.salesAgreementData.expirationDate
          }),
          sap_fields: {
            o_header_sap: [],
            o_item_sap: [],
            o_price_sap: []
          }
        }
      } as CartItemPayload;

      const addItem = await addItemToCart(formattedProduct, currentQuantity, {
        withOptions: false,
        withPrice: false
      });

      if (addItem.item_id) {
        dispatch(
          sendTagging({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: `${product.price.creditPrice}`,
              screenName: commerceScreenName,
              tipo_compra: 'vitrine',
              paginacao: '1',
              items: [
                {
                  screenName: commerceScreenName,
                  price: `${product.price.creditPrice}`,
                  unidade_de_medida: `${product.price.um}`,
                  item_id: `${product.sku}`,
                  item_name: `${product.name}`,
                  tipo_compra: `vitrine`,
                  item_category: `${product.type_id}`,
                  cod_gerdau: `${product.sku}`,
                  preco_acordado: product.price.isAgreementPrice.length > 0,
                  quantity: product.price.quantity ?? '1'
                }
              ]
            }
          })
        );

        setTipoCompraForSku(String(product.sku), 'vitrine');
        setLastTipoCompra('vitrine');

        dispatch(updateCartItemsQuantity(currentQuantity ?? 0));

        dispatch(
          addToast({
            message: `Produto adicionado ao carrinho. (SKU ${product.sku})`,
            type: EToastTypes.SUCCESS,
            durationInMS: 5000,
            spaceToTop: 77
          })
        );
        return;
      }

      throw new Error();
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SearchContainer>
      <SearchField
        ref={ref}
        label=""
        name="search-product-input"
        placeholder="Buscar produtos..."
        inputValue={query}
        onChange={(e) => {
          setQuery(e.target.value.trim());
          debouncedQuery(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        searchResultOptions={query.length > 2 ? searchProductsList : undefined}
        onSelectResultOption={handleSelectedProduct}
        hideLabel
      />
      <Button
        name="button-search-bar"
        variant="primary"
        icon={<Icon option="Search" size="xs" />}
        iconPosition="left"
        onClick={() => searchByString(query)}
      >
        Buscar
      </Button>
    </SearchContainer>
  );
};

export default SearchBar;
